import { Group, SegmentedControl, Table, Text, TextInput, Badge } from '@mantine/core'
import {
  CALIFICACIONES, COLOR_CONDICION, condicionPorPuntaje,
  type CalificacionEntrada, type CriterioFicha,
} from '@serkat/shared'

interface Props {
  criterios: CriterioFicha[]
  valores: CalificacionEntrada[]
  error?: string | undefined
  onCambiar: (valores: CalificacionEntrada[]) => void
}

/**
 * Los criterios del formato, con la escala BUENO/REGULAR/MALO = 3/2/1.
 *
 * «No aplica» excluye el criterio del promedio: la nota al pie del LOG-FT-04 lo
 * permite expresamente. El puntaje se muestra en vivo, pero lo definitivo lo
 * calcula la base al guardar.
 */
export function TablaCriterios({ criterios, valores, error, onCambiar }: Props) {
  const valorDe = (criterioId: number) =>
    valores.find((v) => v.criterioId === criterioId) ?? {
      criterioId, calificacion: null, observaciones: null,
    }

  function editar(criterioId: number, campo: 'calificacion' | 'observaciones', valor: unknown) {
    const existe = valores.some((v) => v.criterioId === criterioId)
    onCambiar(
      existe
        ? valores.map((v) => (v.criterioId === criterioId ? { ...v, [campo]: valor } : v))
        : [...valores, { ...valorDe(criterioId), [campo]: valor }],
    )
  }

  const calificados = valores.filter((v) => v.calificacion !== null)
  const puntaje =
    calificados.length > 0
      ? Math.round((calificados.reduce((s, v) => s + (v.calificacion ?? 0), 0) / calificados.length) * 100) / 100
      : null
  const condicion = condicionPorPuntaje(puntaje)

  return (
    <div>
      <Group justify="space-between" mb="xs">
        <Text fw={550} size="sm">Criterios · Bueno 3 · Regular 2 · Malo 1</Text>
        <Group gap="xs">
          <Text size="sm" c="dimmed">
            {calificados.length} de {criterios.length} calificados
          </Text>
          {puntaje !== null && (
            <>
              <Text size="sm" ff="monospace" fw={600}>{puntaje.toFixed(2)}</Text>
              {condicion && (
                <Badge size="sm" variant="light"
                  color={COLOR_CONDICION[condicion as keyof typeof COLOR_CONDICION]}>
                  {condicion}
                </Badge>
              )}
            </>
          )}
        </Group>
      </Group>

      {error && <Text c="red" size="xs" mb="xs">{error}</Text>}

      <Table.ScrollContainer minWidth={680}>
        <Table withTableBorder verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>N°</Table.Th>
              <Table.Th>Criterio</Table.Th>
              <Table.Th w={260}>Calificación</Table.Th>
              <Table.Th w={220}>Observaciones</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {criterios.map((c) => {
              const v = valorDe(c.id)
              return (
                <Table.Tr key={c.id}>
                  <Table.Td ta="center" c="dimmed">{c.numero}</Table.Td>
                  <Table.Td><Text size="sm">{c.descripcion}</Text></Table.Td>
                  <Table.Td>
                    <SegmentedControl
                      size="xs" fullWidth
                      value={v.calificacion === null ? 'NA' : String(v.calificacion)}
                      onChange={(val) =>
                        editar(c.id, 'calificacion', val === 'NA' ? null : Number(val))
                      }
                      data={[
                        ...CALIFICACIONES.map((k) => ({ value: String(k.valor), label: k.etiqueta })),
                        { value: 'NA', label: 'No aplica' },
                      ]}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput size="xs" value={v.observaciones ?? ''}
                      onChange={(e) => editar(c.id, 'observaciones', e.currentTarget.value || null)} />
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Text size="xs" c="dimmed" mt={6}>
        «No aplica» excluye el criterio del promedio. El puntaje definitivo lo calcula
        el sistema al guardar.
      </Text>
    </div>
  )
}
